import { useMutation, useQueryClient } from '@tanstack/react-query';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import { auth } from '../../../config/firebase';
import { getProfileQueryKey, useProfile } from '../../../shared/hooks/useProfile';
import api from '../../../shared/services/api';

import { UpdatePasswordDto } from '../types/settings.types';

export const useAuthDetails = () => {
  const queryClient = useQueryClient();
  const { authDetails, isLoading } = useProfile();

  // Update password mutation
  const updatePassword = useMutation<{ message: string }, Error, UpdatePasswordDto>({
    mutationFn: async (data: UpdatePasswordDto) => {
      // Verify current password if provided
      if (data.currentPassword && authDetails?.hasPassword) {
        try {
          const userCredential = await signInWithEmailAndPassword(
            auth,
            auth.currentUser?.email || '',
            data.currentPassword
          );
          if (!userCredential.user) {
            throw new Error('Failed to verify current password');
          }
        } catch (error: any) {
          throw new Error('Current password is incorrect');
        }
      }

      const response = await api.put('/user/password', data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Password updated successfully');
      queryClient.invalidateQueries({ queryKey: getProfileQueryKey() });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update password');
    }
  });

  return {
    authDetails,
    isLoading,
    updatePassword: updatePassword.mutate,
    isUpdating: updatePassword.isPending
  };
};
